import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, User, ChevronRight, Tag } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { api } from '@/api';
import { resolveMediaUrl } from '@/utils/helpers';
import { PageHeroBanner } from '@/components/common/PageHeroBanner';
import { SEO } from '@/components/common/SEO';

export const NewsPage = () => {
  const [articles, setArticles] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLeague, setSelectedLeague] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [newsRes, leaguesRes] = await Promise.all([
          api.getNews(),
          api.getLeagues()
        ]);
        setArticles(newsRes.data);
        setLeagues(leaguesRes.data);
      } catch (error) {
        console.error('Error loading news:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const getLeagueName = (leagueId) => {
    const league = leagues.find(l => l.id === leagueId);
    return league?.name || '';
  };

  const filteredArticles = selectedLeague 
    ? articles.filter(a => a.league_ids?.includes(selectedLeague))
    : articles;

  return (
    <div data-testid="news-page">
      <SEO 
        title="News"
        description="Get the latest news and updates from soccer, GAA, hurling, and rugby leagues in the United States. Stay informed about your favorite teams."
        url="/news"
      />
      <PageHeroBanner 
        pageSlug="news" 
        fallbackTitle="News"
        fallbackSubtitle="Latest stories from grassroots sports"
      />
      
      <div className="min-h-screen py-8 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Filter by League */}
          <div className="flex flex-wrap gap-2 mb-8">
            <button
              onClick={() => setSelectedLeague(null)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                !selectedLeague 
                  ? 'bg-sky-500 text-white' 
                  : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
              }`}
            >
              All News
            </button>
            {leagues.slice(0, 8).map(league => (
              <button
                key={league.id}
                onClick={() => setSelectedLeague(league.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  selectedLeague === league.id 
                    ? 'bg-sky-500 text-white' 
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                }`}
              >
                {league.name}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-gray-800/50 rounded-xl overflow-hidden animate-pulse">
                  <div className="h-48 bg-gray-700" />
                  <div className="p-6">
                    <div className="h-4 bg-gray-700 rounded w-3/4 mb-4" />
                    <div className="h-3 bg-gray-700 rounded w-full mb-2" />
                    <div className="h-3 bg-gray-700 rounded w-2/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : filteredArticles.length === 0 ? (
            <div className="text-center py-16">
              <div className="bg-gray-800/50 rounded-xl p-12 max-w-md mx-auto border border-gray-700">
                <p className="text-gray-400 text-lg">No news articles yet.</p>
                <p className="text-gray-500 text-sm mt-2">Check back soon for the latest stories!</p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredArticles.map((article, index) => (
                <Link 
                  key={article.id}
                  to={`/news/${article.slug}`}
                  className={`group bg-gray-800/50 rounded-xl overflow-hidden border border-gray-700 hover:border-sky-500/50 transition-all hover:shadow-lg hover:shadow-sky-500/10 ${
                    index === 0 ? 'md:col-span-2 lg:col-span-2' : ''
                  }`}
                >
                  {/* Featured Image */}
                  <div className={`relative overflow-hidden ${index === 0 ? 'h-64' : 'h-48'}`}>
                    {article.featured_image ? (
                      <img 
                        src={resolveMediaUrl(article.featured_image)} 
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-sky-900 to-blue-900 flex items-center justify-center">
                        <span className="text-4xl">📰</span>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    
                    {/* League Tags */}
                    {article.league_ids?.length > 0 && (
                      <div className="absolute top-4 left-4 flex flex-wrap gap-2">
                        {article.league_ids.slice(0, 2).map(lid => (
                          <Badge key={lid} className="bg-sky-500/80 text-white text-xs">
                            {getLeagueName(lid)}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <h3 className={`font-bold text-white group-hover:text-sky-400 transition-colors mb-3 ${
                      index === 0 ? 'text-2xl' : 'text-lg'
                    }`}>
                      {article.title}
                    </h3>
                    
                    {article.excerpt && (
                      <p className="text-gray-400 text-sm mb-4 line-clamp-2">
                        {article.excerpt}
                      </p>
                    )}

                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <div className="flex items-center gap-4">
                        {article.author && (
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {article.author}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(article.published_at || article.created_at)}
                        </span>
                      </div>
                      <ChevronRight className="w-4 h-4 text-sky-400 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewsPage;
